import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { crearTarea } from '../../services/tareasService.js';
import { getAreas } from '../../services/Admin/areaService.js';
import { getCategoriasByArea } from '../../services/Admin/categoriaService.js';
import { getSubcategoriasByCategoria } from '../../services/Admin/subcategoriaService.js';
import { getDetalleByParametroId } from '../../services/Admin/detalleParametroService.js';
import { getDepartamentosPorDetaparametro } from '../../services/Admin/sedeDepartamentoService.js';
import Sidebar from '../../components/Sidebar';
import { jwtDecode } from 'jwt-decode';
// Asumiendo que tienes un archivo CSS para estilos generales o específicos de formularios
import '../styles/FormPageStyles.css';
import './CrearTareaPage.css';
import '../../components/styles/components.css';

const CrearTareaPage = () => {
  const navigate = useNavigate();

  // --- Lógica del Sidebar ---
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/auth/login');
  };

  const esAdmin = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken.rolId === '1';
    } catch (error) {
      console.error("Error decodificando el token:", error);
      return false;
    }
  };

  // Función para verificar si el usuario es de soporte técnico
  const esSoporteTecnico = () => {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    try {
      const decodedToken = jwtDecode(token);
      return decodedToken.rolId === '3';
    } catch (error) {
      console.error("Error decodificando el token:", error);
      return false;
    }
  };

  const links = [
    { to: '/home', label: 'Home' },
    { to: '/crear-tarea', label: 'Crear Tarea' },
    { to: '/tareas', label: 'Ver Tarea' },
    { to: '/cambiar-contrasena', label: 'Cambiar Contraseña' },
  ];

  if (esAdmin()) {
    links.push({ to: '/admin', label: 'Admin Panel' });
  }

  // Si el usuario es de soporte técnico, añade el enlace de soporte
  if (esSoporteTecnico()) {
    links.push({ to: '/soporte', label: 'Soporte' });
  }
  // --- Fin de la lógica del Sidebar ---

  // Estados para los datos de los selects
  const [areas, setAreas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [servicios, setServicios] = useState([]);

  // Estados para los IDs seleccionados
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [selectedSubcategoria, setSelectedSubcategoria] = useState('');
  const [selectedSede, setSelectedSede] = useState('');
  const [selectedServicio, setSelectedServicio] = useState('');

  // Estados para el formulario
  const [formData, setFormData] = useState({
    observaciones: '',
  });
  const [documentos, setDocumentos] = useState([]);

  // Estados de carga y error
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [loadingSubcategorias, setLoadingSubcategorias] = useState(false);
  const [loadingSedes, setLoadingSedes] = useState(false);
  const [loadingServicios, setLoadingServicios] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar Áreas al montar el componente
  useEffect(() => {
    const fetchAreas = async () => {
      setLoadingAreas(true);
      setError('');
      try {
        const data = await getAreas();
        setAreas(data || []);
      } catch (err) {
        setError('Error al cargar áreas: ' + (err.message || 'Error desconocido'));
        setAreas([]);
      } finally {
        setLoadingAreas(false);
      }
    };
    fetchAreas();
  }, []);

  // Cargar Sedes al montar el componente
  useEffect(() => {
    const fetchSedes = async () => {
      setLoadingSedes(true);
      setError('');
      try {
        const data = await getDetalleByParametroId(7);
        setSedes(data || []);
      } catch (err) {
        setError('Error al cargar sedes: ' + (err.message || 'Error desconocido'));
        setSedes([]);
      } finally {
        setLoadingSedes(false);
      }
    };
    fetchSedes();
  }, []);

  // Cargar Categorías cuando selectedArea cambie
  useEffect(() => {
    if (selectedArea) {
      const fetchCategorias = async () => {
        setLoadingCategorias(true);
        setError('');
        setCategorias([]);
        setSelectedCategoria('');
        setSubcategorias([]);
        setSelectedSubcategoria('');
        try {
          const data = await getCategoriasByArea(selectedArea);
          setCategorias(data || []);
        } catch (err) {
          setError('Error al cargar categorías: ' + (err.message || 'Error desconocido'));
          setCategorias([]);
        } finally {
          setLoadingCategorias(false);
        }
      };
      fetchCategorias();
    } else {
      setCategorias([]);
      setSelectedCategoria('');
      setSubcategorias([]);
      setSelectedSubcategoria('');
    }
  }, [selectedArea]);

  // Cargar Subcategorías cuando selectedCategoria cambie
  useEffect(() => {
    if (selectedCategoria) {
      const fetchSubcategorias = async () => {
        setLoadingSubcategorias(true);
        setError('');
        setSubcategorias([]);
        setSelectedSubcategoria('');
        try {
          const data = await getSubcategoriasByCategoria(selectedCategoria);
          setSubcategorias(data || []);
        } catch (err) {
          setError('Error al cargar subcategorías: ' + (err.message || 'Error desconocido'));
          setSubcategorias([]);
        } finally {
          setLoadingSubcategorias(false);
        }
      };
      fetchSubcategorias();
    } else {
      setSubcategorias([]);
      setSelectedSubcategoria('');
    }
  }, [selectedCategoria]);

  // Cargar Servicios cuando selectedSede cambie
  useEffect(() => {
    const fetchServicios = async () => {
      setLoadingServicios(true);
      setError('');
      setServicios([]);
      setSelectedServicio('');
      try {
        const data = await getDepartamentosPorDetaparametro(selectedSede);
        setServicios(data || []);
      } catch (err) {
        setError('Error al cargar servicios: ' + (err.message || 'Error desconocido'));
        setServicios([]);
      } finally {
        setLoadingServicios(false);
      }
    };

    if (selectedSede) {
      fetchServicios();
    } else {
      setServicios([]);
      setSelectedServicio('');
    }
  }, [selectedSede]);

  const handleAreaChange = (e) => {
    setSelectedArea(e.target.value);
  };

  const handleCategoriaChange = (e) => {
    setSelectedCategoria(e.target.value);
  };

  const handleSubcategoriaChange = (e) => {
    setSelectedSubcategoria(e.target.value);
  };

  const handleSedeChange = (e) => {
    setSelectedSede(e.target.value);
  };

  const handleServicioChange = (e) => {
    setSelectedServicio(e.target.value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setDocumentos([...e.target.files]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!selectedArea || !selectedCategoria || !formData.observaciones) {
      setError('Los campos Área, Categoría y Observaciones son obligatorios.');
      return;
    }

    setIsSubmitting(true);

    const tareaPayload = {
      areaId: selectedArea ? Number(selectedArea) : undefined,
      categoriaId: selectedCategoria ? Number(selectedCategoria) : undefined,
      subcategoriaId: selectedSubcategoria ? Number(selectedSubcategoria) : undefined,
      sedeId: selectedSede ? Number(selectedSede) : undefined,
      servicioId: selectedServicio ? Number(selectedServicio) : undefined,
      observaciones: formData.observaciones,

      documentos: documentos,
    };

    try {
      const result = await crearTarea(tareaPayload);
      if (result.success) {
        setSuccessMessage('¡Tarea creada exitosamente! ID de Tarea: ' + (result.data?.tareaId || result.data?.id || ''));
        setSelectedArea('');
        setSelectedCategoria('');
        setSelectedSubcategoria('');
        setSelectedSede('');
        setServicios([]);
        setSelectedServicio('');
        setFormData({ observaciones: '' });
        setDocumentos([]);
      } else {
        setError(result.message || 'Error al crear la tarea. Intente de nuevo.');
      }
    } catch (err) {
      setError('Error al enviar el formulario: ' + (err.message || 'Error desconocido'));
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="crear-tarea-container">
      <Sidebar links={links} onLogout={handleLogout} />
      <main className="crear-tarea-content">
        <div className="form-wrapper">
          <h2>Crear Nueva Tarea</h2>
          {error && <p className="error-message">{error}</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}
          <form onSubmit={handleSubmit} className="grid-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="area">Área*:</label>
                <select id="area" value={selectedArea} onChange={handleAreaChange} required disabled={loadingAreas}>
                  <option value="">{loadingAreas ? 'Cargando áreas...' : 'Seleccione un Área'}</option>
                  {areas.map(area => (
                    <option key={area.id_area || area.id} value={area.id_area || area.id}>
                      {area.nombre_area || area.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="categoria">Categoría*:</label>
                <select id="categoria" value={selectedCategoria} onChange={handleCategoriaChange} required disabled={!selectedArea || loadingCategorias}>
                  <option value="">{loadingCategorias ? 'Cargando categorías...' : (selectedArea ? 'Seleccione una Categoría' : 'Seleccione un área primero')}</option>
                  {categorias.map(categoria => (
                    <option key={categoria.id_categoria || categoria.id} value={categoria.id_categoria || categoria.id}>
                      {categoria.nombre_categoria || categoria.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="subcategoria">Subcategoría (Opcional):</label>
                <select 
                  id="subcategoria" 
                  value={selectedSubcategoria} 
                  onChange={handleSubcategoriaChange} 
                  disabled={loadingSubcategorias || !selectedCategoria || subcategorias.length === 0}
                >
                  <option value="">
                    {loadingSubcategorias 
                      ? 'Cargando subcategorías...' 
                      : (selectedCategoria ? (subcategorias.length > 0 ? 'Seleccione una Subcategoría' : 'No hay subcategorías') : 'Seleccione categoría')}
                  </option>
                  {subcategorias.map(subcat => (
                    <option key={subcat.id_subcategoria || subcat.id} value={subcat.id_subcategoria || subcat.id}>
                      {subcat.nombre_subcategoria || subcat.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="sede">Sede (Opcional):</label>
                <select id="sede" value={selectedSede} onChange={handleSedeChange} disabled={loadingSedes}>
                  <option value="">{loadingSedes ? 'Cargando sedes...' : 'Seleccione una Sede'}</option>
                  {sedes.map(sede => (
                    <option key={sede.id_detaparametro} value={sede.id_detaparametro}>
                      {sede.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="servicio">Servicio (Opcional):</label>
                <select
                  id="servicio"
                  value={selectedServicio}
                  onChange={handleServicioChange}
                  disabled={loadingServicios || !selectedSede || servicios.length === 0}
                >
                  <option value="">
                    {loadingServicios
                      ? 'Cargando servicios...'
                      : (!selectedSede
                        ? 'Seleccione una sede primero'
                        : (servicios.length > 0 ? 'Seleccione un Servicio' : 'No hay servicios'))}
                  </option>
                  {servicios.map((serv, idx) => (
                    <option
                      key={serv.idSede || serv.id || idx}
                      value={serv.idSede}
                    >
                      {serv.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="observaciones">Descripcion del incombeniente*:</label>
                <textarea
                  id="observaciones"
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleChange}
                  placeholder="Descripcion del incombeniente"
                  required
                />
              </div>



              <div className="form-group">
                <label htmlFor="documentos">Documentos (Opcional):</label>
                <input
                  type="file"
                  id="documentos"
                  name="documentos"
                  multiple
                  onChange={handleFileChange}
                />
              </div>

              
            </div>

            <div className="form-row">
                <button type="submit" className="submit-button create-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Creando Tarea...' : 'Crear Tarea'}
                </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CrearTareaPage; 